import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sprout, Cog, Sparkles } from 'lucide-react';

const Activities = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="activities-title">{t.activities.title}</h1>
          <p className="text-xl">{t.activities.subtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {/* Pole 1: Agro-Alimentaire */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden" data-testid="agro-section">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="h-96 overflow-hidden flex items-center justify-center bg-gray-100">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/5kqww6he_IMG-20260221-WA0006.jpg"
                alt="Pôle Agro-Alimentaire"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center space-x-3 mb-4">
                <Sprout className="text-green-600" size={40} />
                <h2 className="text-3xl font-bold text-stone-800">{t.activities.agro_title}</h2>
              </div>
              <p className="text-lg text-amber-600 font-semibold mb-4">{t.activities.agro_desc}</p>
              <div className="space-y-4 text-gray-700">
                <div>
                  <h3 className="font-bold text-lg mb-2">
                    {language === 'fr' ? 'Barkatis Group Agricole (2016)' : 'Barkatis Group Agricultural (2016)'}
                  </h3>
                  <p>
                    {language === 'fr' ? (
                      "Exploitation de nos propres domaines. Nous cultivons avec passion des vergers d'oliviers et de pistachiers, en respectant les cycles naturels pour obtenir des fruits d'une qualité exceptionnelle."
                    ) : (
                      "Operating our own estates. We passionately cultivate olive and pistachio groves, respecting natural cycles to obtain fruits of exceptional quality."
                    )}
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">Hron Huile (2025)</h3>
                  <p>
                    {language === 'fr' ? (
                      "Notre unité de conditionnement de haute technologie. C'est ici que sont produites et embouteillées nos marques phares, Haroliva et Legato d'oro, selon les standards internationaux les plus rigoureux."
                    ) : (
                      "Our high-tech packaging unit. This is where our flagship brands, Haroliva and Legato d'oro, are produced and bottled according to the most rigorous international standards."
                    )}
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">Disfood (2022)</h3>
                  <p>
                    {language === 'fr' ? (
                      "Notre branche dédiée à la distribution agroalimentaire. Elle assure le lien logistique et commercial pour rendre nos produits accessibles avec une efficacité optimale."
                    ) : (
                      "Our branch dedicated to agri-food distribution. It ensures the logistical and commercial link to make our products accessible with optimal efficiency."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pole 2: Industrie & Technique */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden" data-testid="industry-section">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="p-8 md:p-12 flex flex-col justify-center order-2 md:order-1">
              <div className="flex items-center space-x-3 mb-4">
                <Cog className="text-orange-600" size={40} />
                <h2 className="text-3xl font-bold text-stone-800">{t.activities.industry_title}</h2>
              </div>
              <p className="text-lg text-amber-600 font-semibold mb-4">{t.activities.industry_desc}</p>
              <div className="space-y-4 text-gray-700">
                <div>
                  <h3 className="font-bold text-lg mb-2">
                    {language === 'fr' ? 'Tout pour la Céramique (1993)' : 'Everything for Ceramics (1993)'}
                  </h3>
                  <p>
                    {language === 'fr' ? (
                      "Spécialiste de l'industrie céramique, nous fournissons les matières premières, les équipements de production et les pièces de rechange essentielles à ce secteur."
                    ) : (
                      "Specialist in the ceramic industry, we supply raw materials, production equipment and essential spare parts for this sector."
                    )}
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">
                    {language === 'fr' ? 'Comptoir des Moteurs et Équipements Industriels (2008)' : 'Motors and Industrial Equipment Counter (2008)'}
                  </h3>
                  <p>
                    {language === 'fr' ? (
                      "Importation et distribution de solutions de transmission mécanique. Nous assurons la maintenance et la performance des outils de production grâce à une offre de moteurs et d'équipements industriels de haute qualité."
                    ) : (
                      "Import and distribution of mechanical transmission solutions. We ensure the maintenance and performance of production tools through a range of high-quality motors and industrial equipment."
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="h-96 overflow-hidden flex items-center justify-center bg-gray-100 order-1 md:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/km009rd4_IMG-20260221-WA0005.jpg"
                alt="Pôle Industrie"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Pole 3: Art & Design */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden" data-testid="art-section">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="h-96 overflow-hidden flex items-center justify-center bg-gray-100">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/lic9eacp_IMG-20260221-WA0004.jpg"
                alt="Pôle Art & Design"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center space-x-3 mb-4">
                <Sparkles className="text-purple-600" size={40} />
                <h2 className="text-3xl font-bold text-stone-800">{t.activities.art_title}</h2>
              </div>
              <p className="text-lg text-amber-600 font-semibold mb-4">{t.activities.art_desc}</p>
              <div className="space-y-4 text-gray-700">
                <div>
                  <h3 className="font-bold text-lg mb-2">Sodever (2015)</h3>
                  <p>
                    {language === 'fr' ? (
                      "Unité de création et de production de mosaïques décoratives en verre. Sodever combine savoir-faire artisanal et procédés modernes pour offrir des solutions décoratives uniques destinées à l'architecture et au design d'intérieur."
                    ) : (
                      "Creation and production unit for decorative glass mosaics. Sodever combines traditional craftsmanship with modern processes to offer unique decorative solutions for architecture and interior design."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activities;